'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import PasswordGate from '@/components/ui/PasswordGate';
import {
  Users,
  Search,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  X,
} from 'lucide-react';

interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  brand_name: string | null;
  tier: 'indie_label' | 'local_brand' | 'buyer';
  invite_status: 'pending' | 'invited' | 'failed';
  invited_at: string | null;
  created_at: string;
}

const TIER_LABELS: Record<string, string> = {
  indie_label: 'Indie Labels',
  local_brand: 'Local Brands',
  buyer: 'Buyers',
};

const TIER_COLORS: Record<string, { accent: string; bg: string }> = {
  indie_label: { accent: '#C9A96E', bg: '#FFFBF5' },
  local_brand: { accent: '#8A7560', bg: '#F7F4EF' },
  buyer: { accent: '#1A1A1A', bg: '#F5F3F0' },
};

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ success: number; failed: number } | null>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('waitlist_signups')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setEntries(data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load waitlist data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      router.replace('/login');
    }
  }, [authLoading, user, router]);

  useEffect(() => {
    if (user) fetchEntries();
  }, [user, fetchEntries]);

  const filtered = entries.filter((e) => {
    const matchSearch =
      search === '' ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.email.toLowerCase().includes(search.toLowerCase()) ||
      (e.brand_name || '').toLowerCase().includes(search.toLowerCase());
    const matchTier = tierFilter === 'all' || e.tier === tierFilter;
    const matchStatus = statusFilter === 'all' || e.invite_status === statusFilter;
    return matchSearch && matchTier && matchStatus;
  });

  const tierBreakdown = ['indie_label', 'local_brand', 'buyer'].map((tier) => ({
    tier,
    total: entries.filter((e) => e.tier === tier).length,
    pending: entries.filter((e) => e.tier === tier && e.invite_status === 'pending').length,
    invited: entries.filter((e) => e.tier === tier && e.invite_status === 'invited').length,
  }));

  const totalPending = entries.filter((e) => e.invite_status === 'pending').length;
  const totalInvited = entries.filter((e) => e.invite_status === 'invited').length;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((e) => e.id)));
    }
  };

  const handleBatchInvite = async () => {
    if (selected.size === 0) return;
    setInviting(true);
    setInviteResult(null);

    const toInvite = entries.filter((e) => selected.has(e.id));
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
    const functionUrl = `${supabaseUrl}/functions/v1/send-feedback-email`;
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

    let successCount = 0;
    let failedCount = 0;

    for (const entry of toInvite) {
      try {
        const res = await fetch(functionUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            type: 'beta_invite',
            name: entry.name,
            email: entry.email,
            tier: entry.tier,
            brandName: entry.brand_name,
          }),
        });

        if (res.ok) {
          await supabase
            .from('waitlist_signups')
            .update({ invite_status: 'invited', invited_at: new Date().toISOString() })
            .eq('id', entry.id);
          successCount++;
        } else {
          await supabase
            .from('waitlist_signups')
            .update({ invite_status: 'failed' })
            .eq('id', entry.id);
          failedCount++;
        }
      } catch {
        failedCount++;
      }
    }

    setInviteResult({ success: successCount, failed: failedCount });
    setSelected(new Set());
    setInviting(false);
    fetchEntries();
  };

  const selectedPending = [...selected].filter((id) => {
    const entry = entries.find((e) => e.id === id);
    return entry?.invite_status === 'pending';
  });

  if (authLoading) {
    return (
      <PasswordGate>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#C9A96E', borderTopColor: 'transparent' }} />
        </div>
      </PasswordGate>
    );
  }

  return (
    <PasswordGate>
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FAF7F2' }}>
        {/* Header */}
        <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)' }}>
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center gap-3">
                <AppLogo size={22} />
                <span className="font-serif-display text-lg tracking-tight" style={{ color: '#1A1A1A' }}>
                  Atelier.ai
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#FFFBF5', color: '#C9A96E', border: '1px solid #C9A96E' }}>
                  Admin
                </span>
              </div>
              <Link href="/fashion-discovery-interface" className="text-xs font-medium transition-opacity hover:opacity-70" style={{ color: 'hsl(30 8% 52%)' }}>
                ← Back to App
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 max-w-screen-xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8 space-y-8">
          {/* Page title */}
          <div>
            <h1 className="font-serif-display text-2xl sm:text-3xl" style={{ color: '#1A1A1A' }}>
              Waitlist Dashboard
            </h1>
            <p className="text-sm mt-1" style={{ color: 'hsl(30 8% 52%)' }}>
              Manage beta access invites for Atelier's June 2026 launch.
            </p>
          </div>

          {/* Tier breakdown cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {tierBreakdown.map(({ tier, total, pending, invited }) => {
              const colors = TIER_COLORS[tier];
              return (
                <div
                  key={tier}
                  className="rounded-2xl p-5 border"
                  style={{ backgroundColor: colors.bg, borderColor: 'hsl(35 20% 88%)' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: colors.accent }}>
                      {TIER_LABELS[tier]}
                    </span>
                    <span className="text-2xl font-serif-display" style={{ color: '#1A1A1A' }}>{total}</span>
                  </div>
                  <div className="flex gap-4 text-xs" style={{ color: 'hsl(30 8% 52%)' }}>
                    <span className="flex items-center gap-1">
                      <Clock size={10} style={{ color: colors.accent }} />
                      {pending} pending
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle size={10} style={{ color: '#4CAF50' }} />
                      {invited} invited
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary stats */}
          <div className="flex flex-wrap gap-4 text-sm" style={{ color: 'hsl(30 8% 38%)' }}>
            <span className="flex items-center gap-1.5">
              <Users size={14} style={{ color: '#C9A96E' }} />
              <strong style={{ color: '#1A1A1A' }}>{entries.length}</strong> total signups
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} style={{ color: '#C9A96E' }} />
              <strong style={{ color: '#1A1A1A' }}>{totalPending}</strong> awaiting invite
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle size={14} style={{ color: '#4CAF50' }} />
              <strong style={{ color: '#1A1A1A' }}>{totalInvited}</strong> invited
            </span>
          </div>

          {/* Invite result banner */}
          {inviteResult && (
            <div
              className="rounded-xl px-5 py-3 flex items-center justify-between text-sm border"
              style={{
                backgroundColor: inviteResult.failed === 0 ? '#F0FDF4' : '#FFFBF5',
                borderColor: inviteResult.failed === 0 ? '#86EFAC' : '#C9A96E',
                color: inviteResult.failed === 0 ? '#166534' : '#92400E',
              }}
            >
              <span>
                {inviteResult.success > 0 && `✓ ${inviteResult.success} invite${inviteResult.success > 1 ? 's' : ''} sent successfully.`}
                {inviteResult.failed > 0 && ` ${inviteResult.failed} failed.`}
              </span>
              <button onClick={() => setInviteResult(null)}>
                <X size={14} />
              </button>
            </div>
          )}

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto">
              {/* Search */}
              <div className="relative flex-1 max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'hsl(30 8% 52%)' }} />
                <input
                  type="text"
                  placeholder="Search by name, email, brand…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none"
                  style={{ borderColor: 'hsl(35 20% 88%)', backgroundColor: '#FFFFFF', color: '#1A1A1A' }}
                />
              </div>

              {/* Tier filter */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterMenu((v) => !v)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm"
                  style={{ borderColor: 'hsl(35 20% 88%)', backgroundColor: '#FFFFFF', color: '#1A1A1A' }}
                >
                  {tierFilter === 'all' ? 'All Tiers' : TIER_LABELS[tierFilter]}
                  <ChevronDown size={12} />
                </button>
                {showFilterMenu && (
                  <div
                    className="absolute top-full mt-1 left-0 z-10 rounded-xl border shadow-sm overflow-hidden"
                    style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)', minWidth: '160px' }}
                  >
                    {['all', 'indie_label', 'local_brand', 'buyer'].map((t) => (
                      <button
                        key={t}
                        onClick={() => { setTierFilter(t); setShowFilterMenu(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                        style={{ color: tierFilter === t ? '#C9A96E' : '#1A1A1A' }}
                      >
                        {t === 'all' ? 'All Tiers' : TIER_LABELS[t]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 rounded-xl border text-sm outline-none"
                style={{ borderColor: 'hsl(35 20% 88%)', backgroundColor: '#FFFFFF', color: '#1A1A1A' }}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="invited">Invited</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchEntries}
                className="p-2.5 rounded-xl border transition-opacity hover:opacity-70"
                style={{ borderColor: 'hsl(35 20% 88%)', backgroundColor: '#FFFFFF' }}
                title="Refresh"
              >
                <RefreshCw size={14} style={{ color: 'hsl(30 8% 52%)' }} />
              </button>

              <button
                onClick={handleBatchInvite}
                disabled={selectedPending.length === 0 || inviting}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-opacity disabled:opacity-40"
                style={{ backgroundColor: '#1A1A1A', color: '#FFFFFF' }}
              >
                {inviting ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send size={13} />
                )}
                {inviting ? 'Sending…' : `Send Invite${selectedPending.length > 1 ? 's' : ''} (${selectedPending.length})`}
              </button>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="rounded-xl px-5 py-3 flex items-center gap-2 text-sm border" style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA', color: '#991B1B' }}>
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {/* Table */}
          <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'hsl(35 20% 88%)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#C9A96E', borderTopColor: 'transparent' }} />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Users size={28} style={{ color: 'hsl(35 20% 78%)' }} />
                <p className="text-sm" style={{ color: 'hsl(30 8% 52%)' }}>
                  {entries.length === 0 ? 'No waitlist signups yet.' : 'No results match your filters.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: '#F7F4EF', borderBottom: '1px solid hsl(35 20% 88%)' }}>
                      <th className="px-4 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selected.size === filtered.length && filtered.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded"
                          style={{ accentColor: '#C9A96E' }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-xs tracking-wide uppercase" style={{ color: 'hsl(30 8% 52%)' }}>Name</th>
                      <th className="px-4 py-3 text-left font-medium text-xs tracking-wide uppercase" style={{ color: 'hsl(30 8% 52%)' }}>Email</th>
                      <th className="px-4 py-3 text-left font-medium text-xs tracking-wide uppercase hidden sm:table-cell" style={{ color: 'hsl(30 8% 52%)' }}>Brand</th>
                      <th className="px-4 py-3 text-left font-medium text-xs tracking-wide uppercase" style={{ color: 'hsl(30 8% 52%)' }}>Tier</th>
                      <th className="px-4 py-3 text-left font-medium text-xs tracking-wide uppercase" style={{ color: 'hsl(30 8% 52%)' }}>Status</th>
                      <th className="px-4 py-3 text-left font-medium text-xs tracking-wide uppercase hidden md:table-cell" style={{ color: 'hsl(30 8% 52%)' }}>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((entry, i) => {
                      const colors = TIER_COLORS[entry.tier];
                      const isSelected = selected.has(entry.id);
                      return (
                        <tr
                          key={entry.id}
                          style={{
                            backgroundColor: isSelected ? '#FFFBF5' : i % 2 === 0 ? '#FFFFFF' : '#FAFAF8',
                            borderBottom: '1px solid hsl(35 20% 92%)',
                          }}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(entry.id)}
                              style={{ accentColor: '#C9A96E' }}
                            />
                          </td>
                          <td className="px-4 py-3 font-medium" style={{ color: '#1A1A1A' }}>
                            {entry.name}
                          </td>
                          <td className="px-4 py-3" style={{ color: 'hsl(30 8% 38%)' }}>
                            {entry.email}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell" style={{ color: 'hsl(30 8% 52%)' }}>
                            {entry.brand_name || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ backgroundColor: colors.bg, color: colors.accent, border: `1px solid ${colors.accent}33` }}
                            >
                              {TIER_LABELS[entry.tier]}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {entry.invite_status === 'invited' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#166534' }}>
                                <CheckCircle size={11} /> Invited
                              </span>
                            ) : entry.invite_status === 'failed' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#991B1B' }}>
                                <AlertCircle size={11} /> Failed
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: 'hsl(30 8% 52%)' }}>
                                <Clock size={11} /> Pending
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-xs" style={{ color: 'hsl(30 8% 52%)' }}>
                            {new Date(entry.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {filtered.length > 0 && (
            <p className="text-xs text-right" style={{ color: 'hsl(30 8% 52%)' }}>
              Showing {filtered.length} of {entries.length} signups
            </p>
          )}
        </main>
      </div>
    </PasswordGate>
  );
}
